import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { ISelectOption } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { DynamicMultiSelectFilters } from '../filtersTypes/dynamicFilter.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { InLineControlAiService, StepType } from 'src/app/models/api/agentStep.model';
import { getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { inlineClientApplicationSelectOptions } from 'src/app/models/selectOptions/inlineClientApplicationSelectOptions.model';
import { checkAllGivenIdsExistInOptions, fromIntegrationToSelectOption } from './utils';
import { agentStepsActivityTableColumns } from 'src/app/models/tableColumns/aiAgentStepActivityTableColumn';
import { ITableColumn } from 'src/app/models/tableColumns';
import { IntegrationModel } from 'src/app/models/api';
import moment from 'moment';
import { StepTypeToLabelPipe } from 'src/app/pipes/step-type-to-label.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { beautifyCamelCaseString } from 'src/app/common/stringUtils';
import { AgentNameToLabelPipe } from 'src/app/pipes/agent-name-to-label.pipe';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';

export class AiInLineActivityFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public fromDate: DateFilter;
  public toDate: DateFilter;
  public stepTypeFilters: MultiSelectFilters;
  public actorFilters: DynamicMultiSelectFilters;
  public threadIdFilters: DynamicMultiSelectFilters;
  public integrationFilters: MultiSelectFilters;
  public aiServiceFilters: MultiSelectFilters;
  public clientApplicationFilters: MultiSelectFilters;
  public agentFilters: DynamicMultiSelectFilters;

  public readonly defaultSortColumn: ITableColumn | undefined = agentStepsActivityTableColumns.find(
    (column) => column.sortKey === 'timestamp'
  );
  public readonly sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    this.defaultSortColumn ? { column: this.defaultSortColumn, isDesc: true } : undefined
  );

  private readonly _stepTypeToLabelPipe: StepTypeToLabelPipe;

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _agentNameToLabelPipe: AgentNameToLabelPipe,
    private readonly _permissionsService: PermissionsService
  ) {
    this._stepTypeToLabelPipe = new StepTypeToLabelPipe();
    this._initializeFilters();
    this._integrationsOnUpdate();
    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());
  }

  public getSelectedFilters(): ISelectedFilters {
    return {
      fromDate: this.fromDate.getFilterValue(),
      toDate: this.toDate.getFilterValue(),
      inLineStepType: this.stepTypeFilters.getSelectedFiltersValues(),
      inLineActor: this.actorFilters.getSelectedFiltersValues(),
      inLineThreadId: this.threadIdFilters.getSelectedFiltersValues(),
      inLineIntegration: this.integrationFilters.getSelectedFiltersValues(),
      inLineAiService: this.aiServiceFilters.getSelectedFiltersValues(),
      inLineClientApplication: this.clientApplicationFilters.getSelectedFiltersValues(),
      inLineAgent: this.agentFilters.getSelectedFiltersValues(),
    };
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selectedOptions = [
      ...this.stepTypeFilters.getSelectedOptions(),
      ...this.actorFilters.getSelectedOptions(),
      ...this.threadIdFilters.getSelectedOptions(),
      ...this.integrationFilters.getSelectedOptions(),
      ...this.aiServiceFilters.getSelectedOptions(),
      ...this.clientApplicationFilters.getSelectedOptions(),
      ...this.agentFilters.getSelectedOptions(),
    ];

    return selectedOptions;
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    let needUpdate = false;

    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.FromDate:
          this.fromDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.ToDate:
          this.toDate.update(value as Date | undefined);
          needUpdate = true;
          break;
      }
    }

    if (value instanceof Array) {
      if (value.length == 0) {
        needUpdate = this.clearAllMultiSelectFiltersIfNeeded();
      } else {
        const optionsByCategory: {
          [key: string]: ISelectOption[];
        } = {};
        value.forEach((option) => {
          if (option.filterType) {
            if (option.filterType in optionsByCategory) {
              optionsByCategory[option.filterType].push(option);
            } else {
              optionsByCategory[option.filterType] = [option];
            }
          }
        });
        this._updateAllOptions();

        needUpdate =
          this.stepTypeFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineStepType] || []) || needUpdate;
        needUpdate =
          this.actorFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineActor] || []) || needUpdate;
        needUpdate =
          this.threadIdFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineThreadId] || []) || needUpdate;
        needUpdate =
          this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineIntegration] || []) ||
          needUpdate;
        needUpdate =
          this.aiServiceFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineAiService] || []) || needUpdate;
        needUpdate =
          this.clientApplicationFilters.updateIfNeeded(
            optionsByCategory[FilterTypesEnum.InLineClientApplication] || []
          ) || needUpdate;
        needUpdate =
          this.agentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineAgent] || []) || needUpdate;
      }
    }

    if (needUpdate) {
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: {
          q: filtersQuery,
        },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.InLineStepType:
        return this.stepTypeFilters.getOptions$();
      case FilterTypesEnum.InLineActor:
        return this.actorFilters.getOptions$();
      case FilterTypesEnum.InLineThreadId:
        return this.threadIdFilters.getOptions$();
      case FilterTypesEnum.InLineIntegration:
        return this.integrationFilters.getOptions$();
      case FilterTypesEnum.InLineAiService:
        return this.aiServiceFilters.getOptions$();
      case FilterTypesEnum.InLineClientApplication:
        return this.clientApplicationFilters.getOptions$();
      case FilterTypesEnum.InLineAgent:
        return this.agentFilters.getOptions$();
      default:
        throw new Error(`AiInLineActivityFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};
    options.forEach(
      (option) => {
        if (option.filterType) {
          if (option.filterType in optionsIdsByCategory) {
            optionsIdsByCategory[option.filterType].add(option.id);
          } else {
            optionsIdsByCategory[option.filterType] = new Set([option.id]);
          }
        }
      },
      this.getDynamicFilters().forEach((filter) => {
        optionsIdsByCategory[filter] = new Set();
      })
    );

    if (optionsIdsByCategory[FilterTypesEnum.InLineStepType]) {
      this.stepTypeFilters.updateIfNeeded(
        this.stepTypeFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineStepType] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineStepType].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineActor]) {
      this.actorFilters.updateIfNeeded(
        this.actorFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineActor] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineActor].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineThreadId]) {
      this.threadIdFilters.updateIfNeeded(
        this.threadIdFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineThreadId] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineThreadId].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineIntegration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineIntegration] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineIntegration].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineAiService]) {
      this.aiServiceFilters.updateIfNeeded(
        this.aiServiceFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineAiService] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineAiService].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineClientApplication]) {
      this.clientApplicationFilters.updateIfNeeded(
        this.clientApplicationFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineClientApplication] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineClientApplication].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineAgent]) {
      this.agentFilters.updateIfNeeded(
        this.agentFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineAgent] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineAgent].has(selectedOption.id)
          )
      );
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: {
        q: filtersQuery,
      },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.InLineStepType:
        return this.stepTypeFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineActor:
        return this.actorFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineThreadId:
        return this.threadIdFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineIntegration:
        return this.integrationFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineAiService:
        return this.aiServiceFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineClientApplication:
        return this.clientApplicationFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineAgent:
        return this.agentFilters.getSelectedFiltersIds();
      default:
        throw new Error(`AiInLineActivityFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(
    keyValueFilters: { [key: string]: string[] | string | Date },
    updateQueryParams = false
  ): void {
    let needUpdate = false;
    let values: string[] = [];

    // Handle date filters - support both Date objects and timestamp strings from URL
    if ('fromDate' in keyValueFilters || this.fromDate.getFilterValue() !== undefined) {
      const fromDateValue = keyValueFilters['fromDate'];
      const fromDateMoment =
        fromDateValue && Array.isArray(fromDateValue) && moment(fromDateValue[0], 'x').isValid()
          ? moment(fromDateValue[0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const fromDate = fromDateMoment && !fromDateMoment.isAfter(moment()) ? fromDateMoment.toDate() : undefined;

      this.fromDate.update(fromDate);
      needUpdate = true;
    }

    if ('toDate' in keyValueFilters || this.toDate.getFilterValue() !== undefined) {
      const toDateValue = keyValueFilters['toDate'];
      const toDateMoment =
        toDateValue && Array.isArray(toDateValue) && moment(toDateValue[0], 'x').isValid()
          ? moment(toDateValue[0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const toDate = toDateMoment && !toDateMoment.isAfter(moment()) ? toDateMoment.toDate() : undefined;

      this.toDate.update(toDate);
      needUpdate = true;
    }

    values = 'inLineStepType' in keyValueFilters ? (keyValueFilters['inLineStepType'] as string[]) : [];
    needUpdate = this.stepTypeFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineActor' in keyValueFilters ? (keyValueFilters['inLineActor'] as string[]) : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.actorFilters.getOptions$().value, values)) {
      this.actorFilters = new DynamicMultiSelectFilters([]);
      this._actorOnUpdate(values);
    }
    needUpdate = this.actorFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineThreadId' in keyValueFilters ? (keyValueFilters['inLineThreadId'] as string[]) : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.threadIdFilters.getOptions$().value, values)) {
      this.threadIdFilters = new DynamicMultiSelectFilters([]);
      this._threadIdOnUpdate(values);
    }
    needUpdate = this.threadIdFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineIntegration' in keyValueFilters ? (keyValueFilters['inLineIntegration'] as string[]) : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineAiService' in keyValueFilters ? (keyValueFilters['inLineAiService'] as string[]) : [];
    needUpdate = this.aiServiceFilters.updateByValuesIfNeeded(values) || needUpdate;

    values =
      'inLineClientApplication' in keyValueFilters ? (keyValueFilters['inLineClientApplication'] as string[]) : [];
    needUpdate = this.clientApplicationFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineAgent' in keyValueFilters ? (keyValueFilters['inLineAgent'] as string[]) : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.agentFilters.getOptions$().value, values)) {
      this.agentFilters = new DynamicMultiSelectFilters([]);
      this._agentOnUpdate(values);
    }
    needUpdate = this.agentFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: {
            q: filtersQuery,
          },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public clearAllMultiSelectFiltersIfNeeded(): boolean {
    return (
      this.stepTypeFilters.updateIfNeeded([]) ||
      this.actorFilters.updateIfNeeded([]) ||
      this.threadIdFilters.updateIfNeeded([]) ||
      this.integrationFilters.updateIfNeeded([]) ||
      this.aiServiceFilters.updateIfNeeded([]) ||
      this.clientApplicationFilters.updateIfNeeded([]) ||
      this.agentFilters.updateIfNeeded([])
    );
  }

  public getDynamicFilters(): string[] {
    return [FilterTypesEnum.InLineActor, FilterTypesEnum.InLineThreadId, FilterTypesEnum.InLineAgent];
  }

  private _initializeFilters() {
    this.fromDate = new DateFilter();
    this.toDate = new DateFilter();

    // Initialize step type filter with aiMessageStep, userMessageStep, toolInvocationRequestStep, toolInvocationResultStep, and agent handoff steps for inline
    const inLineStepTypeOptions = [
      StepType.aiMessageStep,
      StepType.userMessageStep,
      StepType.toolInvocationRequestStep,
      StepType.toolInvocationResultStep,
      StepType.agentHandoffRequestStep,
      StepType.agentHandoffResponseStep,
    ]
      .map((stepType) => ({
        id: `${FilterTypesEnum.InLineStepType}-${stepType}`,
        value: stepType,
        displayName:
          this._labelsProviderPipe?.transform(this._stepTypeToLabelPipe.transform(stepType as StepType)) ||
          beautifyCamelCaseString(stepType),
        filterType: FilterTypesEnum.InLineStepType,
        categoryName: FilterTypesEnum.InLineStepType.toString(),
        withoutBeautifyDisplayName: true,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    this.stepTypeFilters = new MultiSelectFilters(inLineStepTypeOptions);
    this.actorFilters = new DynamicMultiSelectFilters([]);
    this.threadIdFilters = new DynamicMultiSelectFilters([]);
    this.integrationFilters = new MultiSelectFilters([]);

    const inLineAiServiceOptions = Object.values(InLineControlAiService)
      .filter((service) => service !== InLineControlAiService.unknown)
      .map((service) => ({
        id: `${FilterTypesEnum.InLineAiService}-${service}`,
        value: service,
        displayName: this._agentNameToLabelPipe.transform(service),
        filterType: FilterTypesEnum.InLineAiService,
        categoryName: FilterTypesEnum.InLineAiService.toString(),
        iconPath: getAiServiceLogoPath(service),
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    this.aiServiceFilters = new MultiSelectFilters(inLineAiServiceOptions);

    // Client Application filter - static list from InlineClientApplication enum
    this.clientApplicationFilters = new MultiSelectFilters(inlineClientApplicationSelectOptions);

    this.agentFilters = new DynamicMultiSelectFilters([]);
    this._updateAllOptions();
  }

  private _threadIdOnUpdate(threadIds: string[]) {
    const threadOptions: ISelectOption[] = [];
    threadIds.forEach((thread) => {
      threadOptions.push({
        id: thread,
        value: thread,
        displayName: thread,
        filterType: FilterTypesEnum.InLineThreadId,
        categoryName: FilterTypesEnum.InLineThreadId.toString(),
        withoutBeautifyDisplayName: true,
      } as ISelectOption);
    });

    const shouldUpdateSelectedOptions = this.threadIdFilters.updateOptions(Object.values(threadOptions));

    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _updateAllOptions() {
    this.allOptions$.next([
      ...this.stepTypeFilters.getOptions$().value,
      ...this.actorFilters.getOptions$().value,
      ...this.threadIdFilters.getOptions$().value,
      ...this.integrationFilters.getOptions$().value,
      ...this.aiServiceFilters.getOptions$().value,
      ...this.clientApplicationFilters.getOptions$().value,
      ...this.agentFilters.getOptions$().value,
    ]);
  }

  private _actorOnUpdate(actors: string[]) {
    const actorOptions: ISelectOption[] = actors.map((actor) => ({
      id: `${FilterTypesEnum.InLineActor}-${actor}`,
      value: actor,
      displayName: actor,
      filterType: FilterTypesEnum.InLineActor,
      categoryName: FilterTypesEnum.InLineActor.toString(),
      withoutBeautifyDisplayName: true,
    }));

    const shouldUpdateSelectedOptions = this.actorFilters.updateOptions(actorOptions);
    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _agentOnUpdate(agents: string[]) {
    const agentOptions: ISelectOption[] = agents.map((agent) => ({
      id: `${FilterTypesEnum.InLineAgent}-${agent}`,
      value: agent,
      displayName: agent,
      filterType: FilterTypesEnum.InLineAgent,
      categoryName: FilterTypesEnum.InLineAgent.toString(),
      withoutBeautifyDisplayName: true,
    }));

    const shouldUpdateSelectedOptions = this.agentFilters.updateOptions(agentOptions);
    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      // Filter integrations based on user's access permissions for step viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.STEP_VIEWER
      );

      const integrationsPlatformSet: Set<string> = new Set();

      const integrationsOptionsMap: ISelectOption[] = filteredIntegrations.map((integration) => {
        integrationsPlatformSet.add(integration.platform);

        const option = fromIntegrationToSelectOption(integration);
        return {
          ...option,
          filterType: FilterTypesEnum.InLineIntegration,
          categoryName: FilterTypesEnum.InLineIntegration.toString(),
        };
      });

      const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }
}
